import React from "react"
import {useTranslation} from "react-i18next"

import {IonAccordion, IonInput, IonItem, IonLabel, IonNote, IonTextarea, IonToggle, useIonAlert} from "@ionic/react"

import {usePersonGroups as useItems} from "../../../contexts/PersonGroupContext"
import PersonGroup from "@customTypes/PersonGroup";

interface componentProps {
    formData:          PersonGroup
    setFormData:       React.Dispatch<React.SetStateAction<PersonGroup>>
    updateFormHandler: any
}

const AccordionPersonGroup: React.FC<componentProps> = (
    {formData, setFormData, updateFormHandler}
) => {

    const { t } = useTranslation()
    const { inProgress } = useItems()
    const [presentAlert] = useIonAlert()

    const alertConfirmDisable = () => {
        return presentAlert({
            header:  t('person-groups.alerts.disable.header'),
            message: t('person-groups.alerts.disable.message'),
            buttons: [{
                text:    t('common.cancel'),
                role:    'cancel',
                handler: () => setFormData({ ...formData, ['isEnabled']: true })
            }, {
                text:    t('common.disable'),
                role:    'confirm',
                handler: () => setFormData({ ...formData, ['isEnabled']: false }),
            },],
        })
    }

    return (
        <IonAccordion toggleIconSlot="start" value="personGroup">
            <IonItem className="bc-accordion" slot="header">
                <IonLabel>{t('person-groups.title_item')}</IonLabel>
            </IonItem>
            {formData.id && formData.dataSourceId ? (
                <IonItem slot="content">
                    <IonLabel>{t('person-groups.form.name')}</IonLabel>
                    <IonNote className="ion-padding-vertical" slot="end">
                        {formData.name}
                    </IonNote>
                </IonItem>
            ) : (
                <IonItem slot="content">
                    <IonInput name="name" type="text"
                              label={t('person-groups.form.name') as string} labelPlacement="stacked"
                              value={formData.name}
                              minlength={2} maxlength={128}
                              placeholder={t('person-groups.form.name') as string}
                              clearInput={true}
                              autofocus={true}
                              required
                              disabled={inProgress}
                              tabIndex={1}
                              onIonChange={updateFormHandler('name')}
                    />
                </IonItem>
            )}

            {formData.id && formData.dataSourceId ? (
                <IonItem slot="content">
                    <IonTextarea label={t('person-groups.form.description').toString()}
                                 label-placement="stacked"
                                 readonly={true}
                                 rows={2}
                                 placeholder={(formData.description && formData.description.length > 1)
                                     ? formData.description
                                     : '(' + t('common.no_description_available').toLowerCase() + ')'} />
                </IonItem>
            ) : (
                <IonItem slot="content">
                    <IonInput name="description" type="text"
                              label={t('person-groups.form.description') as string} labelPlacement="stacked"
                              value={formData.description}
                              minlength={2} maxlength={255}
                              placeholder={t('person-groups.form.description') as string}
                              clearInput={true}
                              disabled={inProgress}
                              tabIndex={2}
                              onIonChange={updateFormHandler('description')}
                    />
                </IonItem>
            )}

            <IonItem slot="content">
                <IonToggle checked={formData.isEnabled} color="success"
                           className={"text-multiline ion-touched " +
                               (formData.isEnabled ? 'ion-valid' : 'ion-invalid')}
                           disabled={inProgress}
                           errorText={t('person-groups.form.is_enabled_error_text').toString()}
                           helperText={t('person-groups.form.is_enabled_helper_text').toString()}
                           justify="space-between" name="isEnabled" labelPlacement="start"
                           onIonChange={(e) => {
                                if (e.target.checked === false) {
                                   return alertConfirmDisable().then()
                                }

                                setFormData({ ...formData, ['isEnabled']: e.target.checked })
                           }}
                           value="1">
                    {t('common.is_enabled')}
                </IonToggle>
            </IonItem>
            <IonItem slot="content">
                <IonToggle checked={formData.defaultNewPersonAddKey} color="success"
                           className={"text-multiline ion-touched " +
                               (formData.defaultNewPersonAddKey ? 'ion-valid' : 'ion-invalid')}
                           disabled={inProgress}
                           errorText={t('person-groups.form.default_new_person_add_key_error_text').toString()}
                           helperText={t('person-groups.form.default_new_person_add_key_helper_text').toString()}
                           justify="space-between" name="defaultNewPersonAddKey" labelPlacement="start"
                           onIonChange={(e) => {
                               setFormData({ ...formData, ['defaultNewPersonAddKey']: e.target.checked })
                           }}
                           value="1">
                    {t('person-groups.form.default_new_person_add_key')}
                </IonToggle>
            </IonItem>
        </IonAccordion>
    )

}

export default AccordionPersonGroup